import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  STATUS_GOOD,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

import { getAttributesForBusinessType, type MappedHeader } from './MapCsvHeaders';

export interface ConfirmCsvUploadDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  fileName: string;
  totalRows: number;
  parseIssuesCount?: number;
  mappedHeaders: MappedHeader[];
  businessUnitType: 'car_dealership' | 'financial_institution' | 'insurance_broker' | 'after_sales';
  businessUnitId?: number;
  confirming?: boolean;
}

const ConfirmCsvUploadDialog: FC<ConfirmCsvUploadDialogProps> = ({
  open,
  onCancel,
  onConfirm,
  fileName,
  totalRows,
  parseIssuesCount = 0,
  mappedHeaders,
  businessUnitType,
  businessUnitId,
  confirming = false,
}) => {
  const attrLabels = getAttributesForBusinessType(businessUnitType, businessUnitId);
  const visibleMappings = mappedHeaders.filter((h) => h.attrCliente !== '');

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            WebkitBackdropFilter: GLASS_BLUR,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW,
            p: 1,
          },
        },
        backdrop: {
          sx: { bgcolor: 'rgba(21, 24, 58, 0.18)', backdropFilter: 'blur(2px)' },
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: RADIUS_ITEM,
              bgcolor: 'rgba(83, 183, 33, 0.12)',
              color: STATUS_GOOD,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckCircleRoundedIcon />
          </Box>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '1.25rem', color: NAVY }}>
            Confirmar subida
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.9rem', color: NAVY, opacity: 0.6, ml: 6.5 }}>
          {fileName}
        </Typography>
      </Box>

      <Box sx={{ px: 3, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_ITEM,
            px: 2,
            py: 1.75,
            display: 'flex',
            alignItems: 'baseline',
            gap: 1,
          }}
        >
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: '1.75rem', color: NAVY }}>
            {totalRows}
          </Typography>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.95rem', color: NAVY }}>
            filas se procesarán
          </Typography>
        </Box>

        {parseIssuesCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(207, 92, 9, 0.08)',
              color: '#CF5C09',
              px: 2,
              py: 1,
              borderRadius: RADIUS_ITEM,
            }}
          >
            <ReportProblemRoundedIcon fontSize="small" />
            <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', color: NAVY }}>
              {parseIssuesCount} fila(s) con problemas de formato. Se reportarán al subir.
            </Typography>
          </Box>
        )}

        <Box>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              color: NAVY,
              opacity: 0.55,
              fontSize: '0.75rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              mb: 1,
            }}
          >
            Mapeo de columnas
          </Typography>
          <Box
            sx={{
              bgcolor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_ITEM,
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {visibleMappings.length === 0 && (
              <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', color: NAVY, opacity: 0.5 }}>
                Sin columnas mapeadas.
              </Typography>
            )}
            {visibleMappings.map((m) => (
              <Box
                key={m.headerName}
                sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 1.5 }}
              >
                <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', color: NAVY, fontWeight: 600 }}>
                  {m.headerName}
                </Typography>
                <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', color: NAVY, opacity: 0.45 }}>
                  →
                </Typography>
                <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', color: NAVY }}>
                  {attrLabels[m.attrCliente] ?? m.attrCliente}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 3, pt: 2, pb: 2.5 }}>
        <Button
          onClick={onCancel}
          disabled={confirming}
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            color: NAVY,
            bgcolor: 'rgba(21, 24, 58, 0.07)',
            borderRadius: RADIUS_COIN,
            px: 2.5,
            textTransform: 'none',
            '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.12)' },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          disabled={confirming}
          sx={{
            bgcolor: YELLOW,
            color: NAVY,
            fontFamily: 'Jost, sans-serif',
            fontWeight: 700,
            borderRadius: RADIUS_COIN,
            px: 3.5,
            boxShadow: SHADOW,
            textTransform: 'none',
            '&:hover': { bgcolor: '#e6c009' },
            '&:disabled': { bgcolor: 'rgba(21,24,58,0.1)', color: 'rgba(21,24,58,0.3)' },
          }}
        >
          {confirming ? 'Subiendo…' : 'Confirmar subida'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ConfirmCsvUploadDialog;
